import { Injectable } from "@angular/core";

import { StoreGradeRecord, StoreGradeResponse } from "../asset-review/store-grade.model";

interface GradeViewState {
  storeUuid: string;
  response: StoreGradeResponse;
  /** Unsaved Generate Fresh Grade result. Never persisted server side. */
  preview: StoreGradeRecord | null;
  activeRecord: StoreGradeRecord | null;
}

/**
 * Survives the Asset Grade card, which does not survive the Full Breakdown.
 *
 * Opening the breakdown flips the spotlight panel to `assetReview`, and the *ngIf around the
 * panel body destroys the card with it -- taking the unsaved preview and the selected history
 * record. The back arrow is specified to restore the panel unchanged, so that state is parked
 * here and picked back up when the card is recreated.
 *
 * One store at a time by design: selecting a different marker must discard the previous store's
 * preview, and a single slot does that without any explicit clearing.
 */
@Injectable({ providedIn: "root" })
export class GradeViewStateService {

  private state: GradeViewState | null = null;

  /**
   * Set while the Full Breakdown is open. Reopening the spotlight panel also destroys and rebuilds
   * the card, but that case is specified to reset to the current record -- so restoring is gated on
   * having actually come back from the breakdown rather than on the store merely matching.
   */
  private fromBreakdown = false;

  save(state: GradeViewState): void {
    this.state = state;
  }

  markBreakdownOpen(): void {
    this.fromBreakdown = true;
  }

  /**
   * Returns the parked state only when the card is being rebuilt behind a closing Full Breakdown,
   * for the same store. Record identity is preserved, so the restored activeRecord still matches
   * the preview or a row of the restored response by reference -- which is what the card's active
   * and current checks compare on.
   */
  restore(storeUuid: string): GradeViewState | null {
    if (!this.fromBreakdown) return null;
    this.fromBreakdown = false;
    return this.state && this.state.storeUuid === storeUuid ? this.state : null;
  }

  clear(): void {
    this.state = null;
    this.fromBreakdown = false;
  }
}
